import { useConversation } from "@elevenlabs/react"
import { atom } from "atom.io"
import { useI, useO } from "atom.io/react"
import React, { useCallback, useEffect } from "react"
import appScreenshot from "./app-screenshot.png"

const timeRemainingAtom = atom<number | undefined>({
	key: "timeRemaining",
	default: undefined,
	effects: [
		({ onSet, setSelf }) => {
			let interval: NodeJS.Timeout | undefined
			onSet(({ oldValue, newValue }) => {
				switch (newValue) {
					case undefined:
						if (interval) {
							clearInterval(interval)
							return
						}
						break
					case 0:
						if (interval) {
							clearInterval(interval)
							return
						}
						setSelf(undefined)
						break
					default:
						if (oldValue === undefined && interval === undefined) {
							interval = setInterval(() => {
								setSelf((current) => {
									switch (current) {
										case undefined:
										case 0:
											clearInterval(interval)
											return undefined
										default:
											return current - 1
									}
								})
							}, 1000)
						}
				}
			})
		},
	],
})

// Simple Button component with inline styles
function Button({
	children,
	onClick,
	disabled = false,
	variant = "default",
}: {
	children: React.ReactNode
	onClick: () => void
	disabled?: boolean
	variant?: "default" | "destructive"
}) {
	const baseStyle = {
		padding: "10px 16px",
		borderRadius: "8px",
		fontWeight: "600",
		transition: "background-color 0.2s ease",
		cursor: disabled ? "not-allowed" : "pointer",
		border: "none",
		fontSize: "16px",
	}

	const variantStyles = {
		default: {
			backgroundColor: disabled ? "#a0c4ff33" : "#1d4ed8",
			color: "white",
		},
		destructive: {
			backgroundColor: disabled ? "#fca5a533" : "#dc2626",
			color: disabled ? "#242424" : "white",
		},
	}

	const combinedStyle = { ...baseStyle, ...variantStyles[variant] }

	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			style={combinedStyle}
		>
			{children}
		</button>
	)
}

export function Conversation() {
	const timeRemaining = useO(timeRemainingAtom)
	const setTimeRemaining = useI(timeRemainingAtom)

	const conversation = useConversation({
		onConnect: () => console.log("Connected"),
		onDisconnect: () => console.log("Disconnected"),
		onMessage: (message) => console.log("Message:", message),
		onError: (error) => console.error("Error:", error),
	})

	const getSignedUrl = async (): Promise<string> => {
		const response = await fetch("/api/get-signed-url")
		if (!response.ok) {
			throw new Error(`Failed to get signed url: ${response.statusText}`)
		}
		const { signedUrl } = await response.json()
		return signedUrl
	}

	const startConversation = useCallback(async () => {
		try {
			// Request microphone permission
			await navigator.mediaDevices.getUserMedia({ audio: true })
			setTimeRemaining(300)
			const signedUrl = await getSignedUrl()

			// Start the conversation with your agent
			await conversation.startSession({
				// connectionType: "webrtc", // either "webrtc" or "websocket"
				signedUrl,
			})
		} catch (error) {
			console.error("Failed to start conversation:", error)
		}
	}, [conversation])

	const endConversation = useCallback(async () => {
		setTimeRemaining(undefined)
		if (conversation.status === "connected") {
			await conversation.endSession()
		}
		window.location.assign("https://virtualwayback.com/customer-discovery")
	}, [conversation])

	useEffect(() => {
		if (!timeRemaining && conversation.status === "connected") {
			endConversation()
		}
	}, [timeRemaining])

	const formatTime = (seconds: number) => {
		const min = Math.floor(seconds / 60)
		const sec = seconds % 60
		return `${min}:${sec.toString().padStart(2, "0")}`
	}

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "space-around",
				height: "calc(100vh - 32px)",
				padding: "0px 16px",
				margin: "16px 0px",
				position: "relative",
			}}
		>
			<img
				src={appScreenshot}
				alt="John Adams"
				style={{
					width: "auto",
					height: "50vh",
					marginBottom: "16px",
					borderRadius: "32px",
				}}
			/>
			<aside
				style={{
					fontSize: "16px",
					textAlign: "center",
					margin: "4px",
					color: "#e8e7e4",
				}}
			>
				Virtual Wayback Audio Demo
			</aside>
			<h1
				style={{
					fontSize: "calc(min(6vw, 32px))",
					fontWeight: "bold",
					textAlign: "center",
					marginBottom: "16px",
					marginTop: "4vw",
					maxWidth: "750px",
				}}
			>
				Step back into 1770 and have a 5-minute talk with 35-year-old attorney,
				John Adams
			</h1>
			<div
				style={{
					fontSize: "15px",
					fontWeight: "normal",
					textAlign: "left",
					color: "#e8e7e4",
					maxWidth: "450px",
					marginBottom: "16px",
					flexShrink: 1,
					flexGrow: 0,
					overflow: "scroll",
					border: "1px solid #e8e7e4",
					borderLeft: "none",
					padding: "16px 8px",
					borderRight: "none",
				}}
			>
				This is an audio demonstration of what Virtual Wayback is building for
				its immersive AI/AR Time Machine into history. The visual overlay map of
				now vs. then is coming soon, but this current audio question/answer
				demonstration will have Adams explain the atmosphere in 1770 Colonial
				Boston, the aftermath of the Boston Massacre and the trial of the
				British Soldiers charged with murder of colonists.
			</div>
			<div
				style={{
					flexGrow: 1,
				}}
			/>
			<div
				style={{
					display: "flex",
					gap: "16px",
				}}
			>
				<Button onClick={startConversation} disabled={Boolean(timeRemaining)}>
					{timeRemaining ? formatTime(timeRemaining) : `Talk with John Adams`}
				</Button>
				<Button
					variant="destructive"
					onClick={endConversation}
					disabled={!timeRemaining}
				>
					End Conversation
				</Button>
			</div>
		</div>
	)
}
